import { motion } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import { QrCode, X, CircleNotch, ShieldCheck } from "@phosphor-icons/react";
import { useLang } from "../context/LangContext";

export const PlatePreview = ({ machine, logoUrl, onClose, onSend, sending, onDownload }) => {
  const { t } = useLang();
  const qrTarget = `${process.env.REACT_APP_BACKEND_URL}/m/${machine.id}`;
  const bg = machine.plate_bg || "#141B2B";
  const h = bg.replace("#", "");
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16) / 255 || 0);
  const dark = 0.299 * r + 0.587 * g + 0.114 * b < 0.5;
  const mainCls = dark ? "text-white" : "text-[#0F172A]";
  const mutedCls = dark ? "text-slate-300" : "text-slate-600";
  const labelCls = dark ? "text-slate-500" : "text-slate-400";
  const lineCls = dark ? "border-white/10" : "border-slate-300";

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0A0F1A]/60 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ opacity: 0, y: 30, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.97 }}
        transition={{ ease: [0.16, 1, 0.3, 1] }} onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white rounded-md border border-slate-200 max-h-[92vh] overflow-y-auto" data-testid="plate-preview-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h3 className="font-display font-bold text-xl text-[#0F172A]">{t.plate.preview}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-[#0F172A]" data-testid="plate-preview-close"><X size={20} /></button>
        </div>

        <div className="p-6">
          <div className="relative rounded-md border p-6 overflow-hidden" style={{ backgroundColor: bg, borderColor: dark ? "rgba(255,255,255,0.1)" : "rgba(15,23,42,0.15)" }} data-testid="plate-preview-plate">
            {[["top-2","left-2"],["top-2","right-2"],["bottom-2","left-2"],["bottom-2","right-2"]].map(([v,h2]) => (
              <span key={v+h2} className={`absolute ${v} ${h2} h-2 w-2 rounded-full ${dark ? "bg-white/20 border-white/30" : "bg-slate-300/60 border-slate-400/50"} border`} />
            ))}

            <div className={`flex items-center justify-between border-b ${lineCls} pb-3`}>
              {logoUrl ? (
                <span className="inline-flex items-center bg-white rounded-[3px] px-1.5 py-1 border border-slate-200" data-testid="plate-preview-client-logo">
                  <img src={logoUrl} alt="logo cliente" className="h-6 w-auto max-w-[110px] object-contain" />
                </span>
              ) : (
                <span className={`font-display font-bold text-sm truncate max-w-[150px] ${mainCls}`} data-testid="plate-preview-client-name">{machine.manufacturer}</span>
              )}
              <div className="flex items-center gap-2">
                <div className="flex h-6 w-6 items-center justify-center bg-emerald-500 rounded-[3px]"><QrCode size={13} weight="bold" className="text-[#04170F]" /></div>
                <span className={`font-display font-bold text-sm ${mainCls}`}>Certifico<span className="text-emerald-500">QR</span></span>
              </div>
            </div>

            <div className="mt-4 flex gap-5">
              <div className="flex-1 min-w-0">
                <p className={`font-display font-bold text-lg leading-tight truncate ${mainCls}`}>{machine.name}</p>
                <div className={`mt-3 space-y-1.5 font-mono-tech text-[11px] ${mutedCls}`}>
                  <p><span className={labelCls}>FAB </span>{machine.manufacturer}</p>
                  <p><span className={labelCls}>MOD </span>{machine.model}</p>
                  <p><span className={labelCls}>S/N </span>{machine.serial_number}</p>
                  {machine.year && <p><span className={labelCls}>ANNO </span>{machine.year}</p>}
                </div>
                <div className={`mt-4 flex items-center gap-2 ${dark ? "text-emerald-400" : "text-emerald-600"}`}>
                  <ShieldCheck size={14} weight="fill" />
                  <span className="font-mono-tech text-[10px] uppercase tracking-wider">UE 2023/1230 · {t.plate.title}</span>
                </div>
              </div>
              <div className="shrink-0">
                <div className="bg-white p-2 rounded-[4px] border border-slate-200" data-testid="plate-preview-qr">
                  <QRCodeSVG value={qrTarget} size={110} level="M" />
                </div>
                <p className={`mt-2 max-w-[126px] text-center font-mono-tech text-[8px] uppercase tracking-wider leading-tight ${labelCls}`}>{t.plate.scan}</p>
              </div>
            </div>
          </div>

          <p className="mt-4 text-xs leading-relaxed text-slate-500">{t.plate.hint}</p>

          <div className="mt-4">
            <p className="font-mono-tech text-[11px] uppercase tracking-wider text-slate-400">{t.plate.download}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {["it", "en", "de", "fr", "es"].map((l) => (
                <button key={l} onClick={() => onDownload(l)}
                  className="rounded-full border border-slate-300 px-3.5 py-1.5 font-mono-tech text-xs font-semibold text-slate-600 hover:border-emerald-500 hover:text-emerald-600 transition-colors"
                  data-testid={`plate-download-${l}`}>{l.toUpperCase()}</button>
              ))}
            </div>
          </div>

          <div className="mt-5 flex gap-3">
            <button onClick={onClose} className="flex-1 rounded-full border border-slate-300 py-3 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors" data-testid="plate-preview-cancel">{t.plate.close}</button>
            <button onClick={onSend} disabled={sending} className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-emerald-500 py-3 text-sm font-semibold text-[#04170F] hover:bg-emerald-400 transition-colors disabled:opacity-60" data-testid="plate-preview-send">
              {sending ? <><CircleNotch size={15} className="animate-spin" /> {t.plate.sending}</> : t.plate.send}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};
